import {DeleteOutlined, EditOutlined, PlusOutlined} from '@ant-design/icons';
import {Breadcrumb, Button, Image, Table, Upload} from 'antd';
import PropTypes from 'prop-types';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {useSelector} from 'react-redux';

import {selectAuth} from '../../../../src/redux/selectors/authSelector';
import {
  RQDeleteHomeImagesAPI,
  RQGetAllHomeImagesAPI,
  RQUpdateHomeImagesAPI,
} from '../../../reactQuery/apis';

import HomeImagesModal from './HomeImagesModal/HomeImagesModal';

const AdminImageTile = ({img, position, onUpload, label, size}) => (
  <div className={'flex flex-col gap-2 w-full h-full'}>

    {/* Label */}
    {(label || size) && (
      <div className={'flex justify-between text-xs font-medium px-1'}>
        <span>{label}</span>
        <span className={'text-gray-400'}>{size}</span>
      </div>
    )}

    <div className={'relative w-full flex-1 rounded-lg overflow-hidden'}>

      {/* Image */}
      {img ? (
        <img
          src={img}
          alt={''}
          className={'absolute inset-0 w-full h-full object-cover'}
        />
      ) : (
        <div className={'absolute inset-0 flex items-center justify-center bg-gray-100'}>
          <PlusOutlined className={'text-2xl text-gray-400'} />
        </div>
      )}

      {/* Overlay (non-blocking) */}
      <div className={'absolute inset-0 bg-black/0 hover:bg-black/40 transition flex items-center justify-center pointer-events-none'}>
        <PlusOutlined className={'text-white text-xl opacity-80'} />
      </div>

      {/* 🔥 Upload MUST be last & highest */}
      <input
        type={'file'}
        accept={'image/*'}
        onChange={e => {
          const file = e.target.files[0];
          if (file) {
            onUpload(file, position);
          }
        }}
        className={'absolute inset-0 w-full h-full opacity-0 cursor-pointer z-20'}
      />

    </div>
  </div>
);
AdminImageTile.propTypes = {
  img: PropTypes.string,
  position: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  onUpload: PropTypes.func.isRequired,
  label: PropTypes.string,  
  size: PropTypes.string,    
};

const HomeImages = () => {
  const {tokenData: token} = useSelector(selectAuth) || {};
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [updateImageData, setUpdateImageData] = useState(null);

  const {data: imageData = [], isLoading, refetch} =
    RQGetAllHomeImagesAPI();

  const homeData = imageData?.[0] || {};

  const openEditModal = () => {
    if (!homeData?.id) {
      toast.error('Home record ID not found!');
      return;
    }
    setUpdateImageData(homeData);
    setIsModalVisible(true);
  };

  const {mutate: deleteImages} = RQDeleteHomeImagesAPI({
    onSuccess: () => {
      toast.success('Images deleted');
      refetch();
    },
  });
  const IMAGE_SIZES = {
    TOP: {width: 1200, height: 400},

    CAROUSEL_WIDE: {width: 500, height: 400},   
    CAROUSEL_TALL: {width: 200, height: 600},  
    CAROUSEL_SQUARE: {width: 200, height: 200},

    MIDDLE: {width: 600, height: 600}, 
  };
  const handleCarouselDirectUpload = (file, position) => {
    if (!homeData?.id || !token) {
      toast.error('Missing Home ID or token');
      return;
    }

    const formData = new FormData();
    formData.append('carouselFiles', file);

    let size;

    if (position === 1) {
      size = IMAGE_SIZES.CAROUSEL_WIDE;
    } else if (position === 2) {
      size = IMAGE_SIZES.CAROUSEL_TALL;
    } else {
      size = IMAGE_SIZES.CAROUSEL_SQUARE;
    }

    formData.append(
      'command',
      JSON.stringify({
        carouselUpdates: [
          {
            position,
            width: size.width,
            height: size.height,
          },
        ],
      }),
    );

    updateHomeImages({token, formData, id: homeData.id});
  };

  const handleMiddleDirectUpload = file => {
    if (!homeData?.id || !token) {
      toast.error('Missing Home ID or token');
      return;
    }

    const formData = new FormData();
    formData.append('middleImage', file);

    formData.append(
      'command',
      JSON.stringify({
        middleImageUpdate: {
          width: IMAGE_SIZES.MIDDLE.width,
          height: IMAGE_SIZES.MIDDLE.height,
        },
      }),
    );

    updateHomeImages({token, formData, id: homeData.id});
  };
  const {mutate: updateHomeImages} = RQUpdateHomeImagesAPI({
    onSuccess: () => {
      toast.success('Home images updated successfully');
      refetch();
      setIsModalVisible(false);
      setUpdateImageData(null);
    },
    onError: () => toast.error('Update failed'),
  });

  const handleUpdateHomeImages = ({id, formData}) => {
    if (!id || !token) {
      toast.error('Missing Home ID or token');
      return;
    }
    updateHomeImages({token, formData, id});
  };

  const getImageByPosition = position =>
    homeData?.carouselImages?.find(i => i.position === position)?.imageUrl ||
    null;

  const columns = [
    {
      title: 'Images',
      dataIndex: 'images',
      render: images => (
        <Image.PreviewGroup>
          {images?.map((img, i) => (
            <Image
              key={i}
              src={img}
              width={200}
              height={90}
              className={'md:p-2'}
              style={{objectFit: 'cover'}}
            />
          ))}
        </Image.PreviewGroup>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      render: isActive => (
        <span className={'font-medium'}>
          {isActive ? 'ACTIVE' : 'INACTIVE'}
        </span>
      ),
    },
    {
      title: 'Action',
      render: (_, record) => (
        <div className={'flex gap-3'}>
          <EditOutlined
            onClick={() => {
              setUpdateImageData(record);
              setIsModalVisible(true);
            }}
          />
          
        </div>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb items={[{title: 'Home'}, {title: 'Home Images'}]} />
      {/* Caraousel Images */}
      <div className={'flex justify-between items-center mt-4'}>
        <h1 className={'font-bold text-lg'}>Home Images</h1>
        <Button type={'primary'} onClick={openEditModal}>
          <PlusOutlined /> Edit Images
        </Button>
      </div>
      <div className={'mt-4 overflow-x-auto'}>
        <Table
          className={'mt-4 gap-2'}
          loading={isLoading}
          columns={columns}
          dataSource={imageData}
          rowKey={'id'}
        /></div>

      <HomeImagesModal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        updateImageData={updateImageData}
        onSubmit={handleUpdateHomeImages}
      />

      {/* Explore More Images */}
      <div>
        <h2 className={'font-bold text-lg mt-8 mb-4'}>Explore More Images</h2>

        <div className={'grid grid-cols-1 md:grid-cols-3 gap-6 max-w-[1400px] items-stretch'}>

          {/* Position 1 */}
          <div className={'h-[200px] w-[310px] md:w-full md:!h-[500px] md:col-span-2 col-span-1'}>
            <AdminImageTile
              img={getImageByPosition(1)}
              position={1}
              label={'Top Banner (Landscape)'}
              size={'500 x 400'}
              onUpload={handleCarouselDirectUpload}
            />
          </div>

          {/* Large Screen Position 2 */}
          <div className={'h-[150px] :w-full row-span-2 md:h-[800px] md:flex hidden'}>
            <AdminImageTile
              img={getImageByPosition(2)}
              position={2}
              label={'Right Banner (Vertical)'}
              size={'200 x 600'}
              onUpload={handleCarouselDirectUpload}
            />
          </div> 

          {/* Positions 3,4,5 */}
          <div className={'flex md:gap-8 gap-3 w-full h-[100px] md:h-[280px]'}>
            {[3, 4, 5].map(pos => (
              <div key={pos} className={'aspect-square'}>
                <AdminImageTile
                  img={getImageByPosition(pos)}
                  position={pos}
                  label={`Small Tile ${pos}`}
                  size={'200 x 200'}
                  onUpload={handleCarouselDirectUpload}
                />
              </div>
            ))}
          </div>

          {/* small Screen Position 2 */}
          <div className={'h-[400px] w-full md:w-full row-span-2 md:h-[800px] flex md:hidden'}>
            <AdminImageTile
              img={getImageByPosition(2)}
              position={2}
              label={'Right Banner (Vertical)'}
              size={'200 x 600'}
              onUpload={handleCarouselDirectUpload}
            />
          </div> 

        </div>
      </div>

      {/* Middle Image */}

      <h2 className={'font-bold text-lg mt-16 mb-3'}>Middle Image</h2>

      <div className={'aspect-square max-w-[600px]'}>
        <AdminImageTile
          img={homeData.middleImage}
          position={'MIDDLE'}
          onUpload={handleMiddleDirectUpload}
        />
      </div>
    </div>
  );
};

export default HomeImages;
